import { CSSProperties, FC } from 'react';
import { Icon } from '@pypestream/design-system';

import backgroundLight from '../../../assets/voice-preview/background-light.png';
import playIconUrl from '../../../assets/voice-preview/play.svg?url';

/*
 * The Preview panel of a language entry, from Figma "Pro Studio (Production)"
 * node 8571:15794. The background is the design's "Background Light" texture
 * under four blend layers; the design rotates that whole stack 180°, which is
 * expressed here as a flipped image and gradients that run bottom-to-top.
 */

const titleCase = (value: string): string =>
  value.replace(/(^|\s)\S/g, (char) => char.toUpperCase());

const RADIUS = 16;

const layer: CSSProperties = {
  position: 'absolute',
  inset: 0,
  borderRadius: RADIUS,
  pointerEvents: 'none',
};

// One line, clipped with an ellipsis inside the card's 16px side insets: a
// long name must never paint outside the card.
const textBase: CSSProperties = {
  margin: 0,
  maxWidth: 218,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

/** The design's text styles (Body 2, Heading H5, Subtitle 2, Caption). */
const TEXT = {
  previewLabel: {
    ...textBase,
    fontSize: 14,
    lineHeight: '24px',
    fontWeight: 400,
    color: '#cec7f5',
  },
  voiceName: {
    ...textBase,
    fontSize: 20,
    lineHeight: '32px',
    fontWeight: 500,
    color: '#ffffff',
  },
  language: {
    ...textBase,
    fontSize: 14,
    lineHeight: '24px',
    fontWeight: 500,
    color: '#ffffff',
  },
  accentPill: {
    ...textBase,
    fontSize: 11,
    lineHeight: '16px',
    fontWeight: 400,
    color: '#5956e3',
    background: '#e7e3fa',
    padding: '4px 8px',
    borderRadius: 100,
  },
} satisfies Record<string, CSSProperties>;

/** "Audio_wave" (8571:15806): five white bars in a 60×60 box. */
const AudioWave: FC = () => (
  <svg width="60" height="60" viewBox="0 0 60 60" aria-hidden="true">
    {[
      { x: 3.75, y: 26.25, h: 7.5 },
      { x: 15, y: 22.5, h: 15 },
      { x: 26.25, y: 20.63, h: 16.87 },
      { x: 37.5, y: 22.5, h: 15 },
      { x: 48.75, y: 26.25, h: 7.5 },
    ].map(({ x, y, h }) => (
      <rect
        key={x}
        x={x}
        y={y}
        width="7.5"
        height={h}
        rx="3.75"
        fill="#ffffff"
      />
    ))}
  </svg>
);

export interface VoicePreviewCardProps {
  voiceName: string;
  languageName: string;
  accent?: string;
  isPlaying: boolean;
  canPlay: boolean;
  onTogglePlay: () => void;
  testId: string;
}

export const VoicePreviewCard: FC<VoicePreviewCardProps> = ({
  voiceName,
  languageName,
  accent,
  isPlaying,
  canPlay,
  onTogglePlay,
  testId,
}) => (
  <div
    {...{ 'test-id': testId }}
    style={{
      position: 'relative',
      width: 250,
      minHeight: 411,
      flexShrink: 0,
      alignSelf: 'stretch',
      borderRadius: RADIUS,
      overflow: 'hidden',
      isolation: 'isolate',
    }}
  >
    <div aria-hidden="true" style={{ ...layer, overflow: 'hidden' }}>
      <img
        src={backgroundLight}
        alt=""
        style={{
          ...layer,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'top',
          transform: 'rotate(180deg)',
        }}
      />
      <div
        style={{
          ...layer,
          background: 'rgba(133, 115, 231, 0.6)',
          mixBlendMode: 'multiply',
        }}
      />
      <div
        style={{
          ...layer,
          background: 'rgba(133, 115, 231, 0.5)',
          mixBlendMode: 'soft-light',
        }}
      />
      <div
        style={{
          ...layer,
          background:
            'linear-gradient(to top, rgba(133, 115, 231, 0) 0%, rgba(79, 74, 137, 0.3) 62.679%)',
          mixBlendMode: 'overlay',
        }}
      />
      <div
        style={{
          ...layer,
          background:
            'linear-gradient(to top, rgba(133, 115, 231, 0) 34.62%, rgba(79, 74, 137, 0.8) 79.607%)',
        }}
      />
    </div>

    <div
      style={{
        position: 'relative',
        height: '100%',
        minHeight: 411,
        boxSizing: 'border-box',
        padding: '40px 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <p style={TEXT.previewLabel}>Preview</p>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <p style={TEXT.voiceName}>{voiceName}</p>
          <p style={TEXT.language}>{languageName}</p>
          {accent && <span style={TEXT.accentPill}>{titleCase(accent)}</span>}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 53,
        }}
      >
        <AudioWave />
        <button
          type="button"
          onClick={onTogglePlay}
          disabled={!canPlay}
          // aria-pressed carries playing/stopped; the name stays fixed.
          aria-label={`Play preview of ${voiceName}`}
          aria-pressed={isPlaying}
          {...{ 'test-id': `${testId}-play-button` }}
          style={{
            width: 42,
            height: 42,
            padding: 8.4,
            boxSizing: 'border-box',
            borderRadius: 105,
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#ffffff',
            cursor: canPlay ? 'pointer' : 'not-allowed',
            opacity: canPlay ? 1 : 0.6,
          }}
        >
          {isPlaying ? (
            <Icon name="recording" size="small" color="purple" />
          ) : (
            <img src={playIconUrl} alt="" width="25.2" height="25.2" />
          )}
        </button>
      </div>
    </div>
  </div>
);
